import { ShopItemAdapter } from "../adapters/types";
import { z } from "zod";

import { ItemType } from "@/types/enums/item-type";
import { GiftCardPurchaseLineInput, GiftCardShopCartLine } from "@/types/shop";

/**
 * Pre-checkout invariants for a gift-card line. Mirrors the constraints the
 * server enforces in `GiftCardPurchaseLineInput._validate`, but at the cart
 * level — runs on every add/edit/quantity change via the cart hook and at
 * checkout time so a malformed line never reaches `/shop/payin-config`.
 */
const recipientSchema = z
  .object({
    email: z.string().nullish(),
    phone: z.string().nullish(),
  })
  .refine((r) => Boolean(r.email?.trim() || r.phone?.trim()), {
    message: "Each recipient needs an email or phone number.",
  });

const lineSchema = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("preset"),
      quantity: z
        .number()
        .int()
        .min(1, "Quantity must be at least 1.")
        .max(50, "Maximum 50 gift cards per line."),
      unitPriceCents: z.number().int().positive("Gift card price is invalid."),
      recipients: z.array(recipientSchema),
    }),
    z.object({
      kind: z.literal("custom"),
      quantity: z
        .number()
        .int()
        .min(1, "Quantity must be at least 1.")
        .max(50, "Maximum 50 gift cards per line."),
      amountCents: z
        .number()
        .int()
        .positive("Custom gift cards must have a positive amount."),
      recipients: z.array(recipientSchema),
    }),
  ])
  // Recipients describe the cards sent to other people; the remaining
  // `quantity - recipients.length` cards stay with the buyer. So any
  // count from 0 up to `quantity` is valid.
  .refine((line) => line.recipients.length <= line.quantity, {
    message: "Cannot have more recipients than gift cards.",
  });

const validate = (line: GiftCardShopCartLine): string | null => {
  const result = lineSchema.safeParse(line);
  if (result.success) return null;
  return result.error.issues[0]?.message ?? "Gift card line is invalid.";
};

const serializeForCheckout = (
  line: GiftCardShopCartLine,
): GiftCardPurchaseLineInput => {
  const recipients = line.recipients.length > 0 ? line.recipients : null;
  const base = {
    itemType: ItemType.GIFT_CARD as const,
    quantity: line.quantity,
    recipients,
  };
  if (line.kind === "preset") {
    return { ...base, itemId: line.itemId };
  }
  return { ...base, amountCents: line.amountCents };
};

// Recipients ≤ quantity is valid (the remainder stays with the buyer). If a
// stale cart somehow holds more recipients than cards — e.g. someone edited
// quantity down outside the configurator — trim the trailing entries rather
// than blowing the whole list away, so the buyer doesn't lose the recipients
// they typed in.
const normalizeLine = (line: GiftCardShopCartLine): GiftCardShopCartLine => {
  if (line.recipients.length <= line.quantity) return line;
  return { ...line, recipients: line.recipients.slice(0, line.quantity) };
};

export const giftCardAdapter: ShopItemAdapter<
  GiftCardShopCartLine,
  GiftCardPurchaseLineInput
> = {
  type: ItemType.GIFT_CARD,
  validate,
  serializeForCheckout,
  normalizeLine,
};
